-- | Step 3 rendering.
-- |
-- | This module renders the live Step 3 tree. It does not decide navigation
-- | semantics or write browser history.
module Steps.Step3.View
    ( renderStep3
    ) where

import Common

import API.SelStudent (studentName)
import App.Route as AppRoute
import Concur.React.DOM as D
import Concur.React.Props as P
import Data.Array (catMaybes)
import Data.Maybe (Maybe(..), isNothing)
import Effect.Class (liftEffect)
import Internal.Util (justIf)
import React.SyntheticEvent (preventDefault)
import Steps.Step3.FormView (renderFormFill)
import Steps.Step3.State (FormFillRenderOptions, Step3State, isBusy, shouldSaveCurrentDocument)
import Steps.Step3.Types (FormFillResult(..), hasEditableFields, onFirstItem, onLastItem)
import Steps.WorkQueue.State (formForSelected')
import Steps.WorkQueue.View (WorkQueueResult(..), workQueueWidgetWithRoute)
import Widgets.Dropdown (dropdownButton, dropdownButton_)

renderStep3 :: FormFillRenderOptions -> AppRoute.PreservedQuery -> Step3State -> Widget HTML FormFillResult
renderStep3 renderOptions preservedQuery state =
    let busy = isBusy state
    in
    D.main
        [ P.className "step-3-scroll overflow-y-auto", P.role "main" ]
        [ D.div
            [ P.className "step-container step-3"
            , P.key "step-3-container"
            , P._id "step-3-container"
            ]
            $ catMaybes
                [ justIf renderOptions.sideAndTopBar $ renderHeader busy state
                , justIf renderOptions.sideAndTopBar $ renderWorkQueue preservedQuery state
                , Just $ renderEditorPane renderOptions busy state
                ]
        ]

renderWorkQueue :: AppRoute.PreservedQuery -> Step3State -> Widget HTML FormFillResult
renderWorkQueue preservedQuery state = do
    result <- workQueueWidgetWithRoute preservedQuery state.editor.workQueueState state.editor.workQueueUi state.editor.workQueueCache
    case result of
        jump@(JumpToSection _ event) -> do
            liftEffect $ preventDefault event
            pure $ WorkQF jump
        other ->
            pure $ WorkQF other

renderHeader :: Boolean -> Step3State -> Widget HTML FormFillResult
renderHeader busy state = D.header
    [ P.className "page-header" ]
    [ D.div
        [ P.className "page-headings" ]
        [ D.h1
            [ P.className "page-heading" ]
            [ D.text $ studentName state.editor.workQueueCache.account state.editor.fillState.doc.student ]
        , D.h2
            [ P.className "page-subheading" ]
            [ D.text state.editor.fillState.form.label ]
        ]
    , D.div
        [ P.className "btn-group" ] $
            catMaybes
                [ Just $ D.button
                    [ CancelF <$ P.onClick
                    , P.className "btn btn-cancel"
                    , P.disabled busy
                    ]
                    [ D.text $ lookupTranslation "cancel" ]
                , Just $ renderExportDropdown busy state
                , Just $ D.button
                    [ SaveF <$ P.onClick
                    , P.className "btn btn-save btn-primary"
                    , P.disabled disableSave
                    ]
                    [ D.text $ lookupTranslation "save" ]
                , justIf (not $ onFirstItem state.editor) $ D.button
                    [ PrevF <$ P.onClick
                    , P.className "btn btn-previous"
                    ]
                    [ D.text $ lookupTranslation "prev" ]
                , Just $ D.button
                    [ NextF <$ P.onClick
                    , P.className "btn btn-next btn-primary"
                    , P.disabled disableNext
                    ]
                    [ D.text $ if onLastItem state.editor
                        then lookupTranslation "finish"
                        else lookupTranslation "next"
                    ]
                , Just $ dropdownButton_ dropBtn $ autofillItem <> lockToggleItem
                ]
    ]
  where
    fillState = state.editor.fillState
    workQueueState = state.editor.workQueueState
    disableSave = busy || not (hasEditableFields fillState) || not (formForSelected' workQueueState)
    disableNext = if onLastItem state.editor
        then busy || not (formForSelected' workQueueState)
        else not (formForSelected' workQueueState)

    autofillItem =
        if busy
            then
                [ D.a
                    [ P.className "dropdown-item disabled whitespace-nowrap autofill-form-action" ]
                    [ D.text $ lookupTranslation "autofill-form-button" ]
                ]
            else if isNothing fillState.doc.locked_at
                then
                    [ D.a
                        [ AutofillF <$ P.onClick
                        , P.className "dropdown-item whitespace-nowrap autofill-form-action"
                        ]
                        [ D.text $ lookupTranslation "autofill-form-button" ]
                    ]
                else
                    [ D.a
                        [ P.className "dropdown-item disabled whitespace-nowrap autofill-form-action"
                        , P.title $ lookupTranslation "autofill-disabled-reason-lock"
                        ]
                        [ D.text $ lookupTranslation "autofill-form-button" ]
                    ]

    lockToggleItem =
        if fillState.doc.can.lock
            then
                [ D.a
                    [ if busy then P.className "dropdown-item disabled whitespace-nowrap"
                        else ToggleLockF <$ P.onClick
                    , P.className $ "dropdown-item whitespace-nowrap " <> lockToggleActionClass fillState.doc.locked_at <> if busy then " disabled" else ""
                    ]
                    [ D.text $ lockToggleLabel (shouldSaveCurrentDocument state) fillState.doc.locked_at ]
                ]
            else []

    dropBtn = D.button
        [ P.className "dropdown-button"
        , P.onClick
        ]
        [ D.text "⠇" ]

    lockToggleLabel :: forall a. Boolean -> Maybe a -> String
    lockToggleLabel true Nothing = lookupTranslation "save-and-lock"
    lockToggleLabel _ Nothing = lookupTranslation "lock"
    lockToggleLabel _ (Just _) = lookupTranslation "unlock"

    lockToggleActionClass :: forall a. Maybe a -> String
    lockToggleActionClass Nothing = "lock-document-action"
    lockToggleActionClass (Just _) = "unlock-document-action"


renderExportDropdown :: Boolean -> Step3State -> Widget HTML FormFillResult
renderExportDropdown _ { editor: { fillState: { doc: { exports: [] } } } } = D.div' []
renderExportDropdown busy { editor: { fillState: { doc: { exports: [ report ] } } } } = D.button
    [ ExportF report <$ P.onClick
    , P.className "btn btn-export"
    , P.disabled busy
    ]
    [ D.text $ lookupTranslation "export" ]
renderExportDropdown busy state = ExportF <$>
    dropdownButton button items
  where
    ffs = state.editor.fillState
    button = D.button
        [ P.onClick
        , P.disabled (busy || not (formForSelected' state.editor.workQueueState))
        , P.className "btn btn-export"
        ]
        [ D.text $ lookupTranslation "export" ]
    mkItem export = { label: export.label, value: export }
    items = map mkItem ffs.doc.exports

renderEditorPane :: FormFillRenderOptions -> Boolean -> Step3State -> Widget HTML FormFillResult
renderEditorPane renderOptions busy state =
    renderFormFill renderOptions.signatures busy state.editor
